import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Input, OnDestroy, Output, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { PartnerOverviewData } from '../unpaid-partner-tile-summary.service';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { TooltipComponent } from '../../../../shared/components/ui/tooltip/tooltip.component';
import { UnpaidPaymentsCalculationService } from '../unpaid-payments-calculation.service';

@Component({
  selector: 'app-unpaid-partner-overview-cards',
  standalone: true,
  imports: [CommonModule, IconComponentV2, TooltipComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './unpaid-partner-overview-cards.component.html',
})
export class UnpaidPartnerOverviewCardsComponent implements OnDestroy {
  @Input() data: PartnerOverviewData | null = null;
  /** Initial list fetch — skeleton all overview cards. */
  @Input() loading = false;
  /** Payable totals still computing — skeleton action cards only. */
  @Input() calculating = false;
  @Input() payAllDisabled = false;
  @Input() payUrgentDisabled = false;
  @Input() showUrgentAction = false;
  /** Hides the urgent amount line; shows payment count as the primary metric (e.g. lendee filter). */
  @Input() hideUrgentAmount = false;
  @Input() showPayAllAction = true;
  /** Hides the amount and payment count in the "Amount To Pay" card. */
  @Input() hideAmount = false;
  /** Overview "Pay all" button label (varies by FM filter / status). */
  @Input() payAllLabel = 'Pay All to Businesses';
  /** Renders "Ask Prism AI" in the overview header (e.g. Lendee). */
  @Input() showAskPrismAi = false;

  /** "As of" line on the balance card (refreshes per component instance). */
  readonly overviewAsOfDate = new Date();
  readonly isDialogOpen = signal(false);
  readonly overviewTooltipZIndex = computed(() => this.isDialogOpen() ? 1 : 99999);
  private readonly dialogSubscriptions = new Subscription();

  constructor(
    private readonly calculationService: UnpaidPaymentsCalculationService,
    private readonly dialog: MatDialog
  ) {
    this.syncDialogOpenState();
    this.dialogSubscriptions.add(
      this.dialog.afterOpened.subscribe(() => this.isDialogOpen.set(true))
    );
    this.dialogSubscriptions.add(
      this.dialog.afterAllClosed.subscribe(() => this.isDialogOpen.set(false))
    );
  }

  ngOnDestroy(): void {
    this.dialogSubscriptions.unsubscribe();
  }

  get upcomingAmountTooltip(): string {
    return this.calculationService.formatUpcomingAmountTooltip(this.data?.upcomingAmount ?? 0);
  }

  private syncDialogOpenState(): void {
    this.isDialogOpen.set(this.dialog.openDialogs.length > 0);
  }

  @Output() payAllToBusiness = new EventEmitter<void>();
  @Output() payUrgent = new EventEmitter<void>();
}
